// src/components/BudgetVsActual.js
// Budget vs Actual: budgeted amount vs actual GL movement per income-statement
// account for an academic year + term, with variance.
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Card, CardContent, Typography, Table, TableHead, TableBody, TableRow,
  TableCell, TextField, MenuItem, Alert, CircularProgress, IconButton
} from '@mui/material';
import { Home, Print, TrendingUp, Description } from '@mui/icons-material';
import axios from 'axios';
import { fmt } from '../utils/currency';
import CompanyHeader from './CompanyHeader';
import { useCompany } from '../hooks/useCompany';
import { downloadCsv } from '../utils/csvExport';

const API  = process.env.REACT_APP_API_URL;
const hdrs = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });
const TERMS = [1, 2, 3];

// For Revenue, positive variance (actual > budget) is favourable → green.
// For Expense, negative variance (actual < budget) is favourable → green.
const varColor = (variance, type) => {
  if (Math.abs(variance) < 0.005) return 'text.secondary';
  const favourable = type === 'Revenue' ? variance > 0 : variance < 0;
  return favourable ? 'success.main' : 'error.main';
};

const BudgetVsActual = () => {
  const navigate = useNavigate();
  const company = useCompany();
  const [years, setYears]       = useState([]);
  const [academicYear, setYear] = useState('');
  const [term, setTerm]         = useState(1);
  const [data, setData]         = useState(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');

  useEffect(() => {
    axios.get(`${API}/api/academics/years`, { headers: hdrs() })
      .then(r => {
        const ys = Array.isArray(r.data) ? r.data : [];
        setYears(ys);
        const active = ys.find(y => y.isActive) || ys[0];
        if (active) setYear(active.year);
      }).catch(() => {});
  }, []);

  const run = useCallback(async () => {
    if (!academicYear || !term) return;
    setLoading(true); setError('');
    try {
      const { data } = await axios.get(`${API}/api/finance/reports/budget-vs-actual`, { headers: hdrs(), params: { academicYear, term } });
      setData(data);
    } catch (err) { setError(err.response?.data?.error || 'Failed to load report'); }
    finally { setLoading(false); }
  }, [academicYear, term]);

  useEffect(() => { run(); }, [run]);

  const exportExcel = () => {
    if (!data) return;
    const line = (r) => [r.accountNo, r.accountName, r.budget, r.actual, r.variance];
    const rows = [
      [company?.name || 'BUDGET VS ACTUAL'],
      ['BUDGET VS ACTUAL'],
      [`${academicYear} · Term ${term}`],
      [`Generated: ${new Date().toLocaleDateString()}`],
      [],
      ['Account No', 'Name', 'Budget', 'Actual', 'Variance'],
      ['INCOME'],
      ...data.revenue.map(line),
      ['', 'Total Income', data.totals.revenue.budget, data.totals.revenue.actual, data.totals.revenue.variance],
      [],
      ['EXPENSES'],
      ...data.expense.map(line),
      ['', 'Total Expenses', data.totals.expense.budget, data.totals.expense.actual, data.totals.expense.variance],
    ];
    downloadCsv(rows, `budget_vs_actual_${academicYear}_T${term}.csv`);
  };

  const renderSection = (title, list, type, totals, color) => (
    <>
      <TableRow><TableCell colSpan={4} sx={{ fontWeight: 800, color, pt: 2 }}>{title}</TableCell></TableRow>
      {list.length === 0 ? (
        <TableRow><TableCell colSpan={4}><Typography variant="body2" color="text.secondary">None</Typography></TableCell></TableRow>
      ) : list.map(r => (
        <TableRow key={r.accountNo} hover>
          <TableCell>{r.accountNo} — {r.accountName}</TableCell>
          <TableCell align="right">{fmt(r.budget)}</TableCell>
          <TableCell align="right">{fmt(r.actual)}</TableCell>
          <TableCell align="right" sx={{ color: varColor(r.variance, type), fontWeight: 600 }}>{fmt(r.variance)}</TableCell>
        </TableRow>
      ))}
      <TableRow>
        <TableCell sx={{ fontWeight: 700 }}>Total {title}</TableCell>
        <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(totals.budget)}</TableCell>
        <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(totals.actual)}</TableCell>
        <TableCell align="right" sx={{ fontWeight: 700, color: varColor(totals.variance, type) }}>{fmt(totals.variance)}</TableCell>
      </TableRow>
    </>
  );

  return (
    <Box sx={{ maxWidth: 900, mx: 'auto', mt: 4, mb: 6 }}>
      <style>{`@media print { .no-print { display:none !important; } body { background:#fff !important; } .bva-card { box-shadow:none !important; } }`}</style>
      <Box display="flex" gap={1.5} mb={2} className="no-print" alignItems="center" flexWrap="wrap">
        <TextField select label="Academic Year" value={academicYear} size="small" sx={{ minWidth: 140 }}
          onChange={e => setYear(e.target.value)}>
          {years.map(y => <MenuItem key={y.year} value={y.year}>{y.year}</MenuItem>)}
        </TextField>
        <TextField select label="Term" value={term} size="small" sx={{ minWidth: 100 }}
          onChange={e => setTerm(Number(e.target.value))}>
          {TERMS.map(t => <MenuItem key={t} value={t}>Term {t}</MenuItem>)}
        </TextField>
        <IconButton onClick={run} disabled={loading} color="primary" sx={{ bgcolor: '#2e7d3215' }}>
          {loading ? <CircularProgress size={20} /> : <TrendingUp />}
        </IconButton>
        <Box flex={1} />
        <IconButton onClick={exportExcel} title="Export to Excel" disabled={!data}
          sx={{ bgcolor: 'background.paper', color: 'success.main', '&:hover': { bgcolor: 'grey.100' } }}><Description /></IconButton>
        <IconButton onClick={() => window.print()} title="Print" disabled={!data}
          sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}><Print /></IconButton>
        <IconButton onClick={() => navigate('/finance')} title="Finance Home"
          sx={{ bgcolor: 'background.paper', '&:hover': { bgcolor: 'grey.100' } }}><Home /></IconButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Card elevation={3} className="bva-card">
        <CardContent sx={{ p: { xs: 2, sm: 4 } }}>
          <CompanyHeader title="BUDGET VS ACTUAL" subtitle={`${academicYear} · Term ${term}`} />
          {data && (
            <>
              {!data.from && !data.to && (
                <Alert severity="info" sx={{ mb: 2 }} className="no-print">
                  This term has no start/end dates configured, so actuals cover all GL entries. Set term dates on the Academic Year for period-accurate actuals.
                </Alert>
              )}
              <Box sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell><strong>Account</strong></TableCell>
                      <TableCell align="right"><strong>Budget</strong></TableCell>
                      <TableCell align="right"><strong>Actual</strong></TableCell>
                      <TableCell align="right"><strong>Variance</strong></TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {renderSection('Income', data.revenue, 'Revenue', data.totals.revenue, '#2e7d32')}
                    {renderSection('Expenses', data.expense, 'Expense', data.totals.expense, '#c62828')}
                  </TableBody>
                </Table>
              </Box>
            </>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default BudgetVsActual;
